import { UserGroupMemberRole } from '@navio/nsdk';
import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import { noddiToast, useAppForm } from 'noddi-ui-web';
import { employeeForcedByAdmin } from '../UserGroups/membershipRole';
import { buildMemberSchema, type MemberFormValues } from './memberFormSchema';
import {
  buildAccountPatch,
  buildMembershipPatch,
  keepEditedValues,
  type MemberSheetPermissions
} from './memberSheetPatches';
import { MemberGroup } from './notificationGroupTypes';
import { NotificationGroupOverviewTranslations } from './notificationTranslations';

type Args = {
  userGroupId: number;
  member: MemberGroup;
  permissions: MemberSheetPermissions;
  translations: NotificationGroupOverviewTranslations;
  /** See `CarContactsOverview` — only the customer app needs its own success copy. */
  showMutationToasts: boolean;
  onClose: () => void;
};

/**
 * The sheet's form and the two requests behind its single Save. The account half
 * and the membership half are separate endpoints with separate permissions, so
 * each is sent only when the viewer owns it and actually changed it.
 */
export const useMemberSheetForm = ({
  userGroupId,
  member,
  permissions,
  translations: t,
  showMutationToasts,
  onClose
}: Args) => {
  const invalidate = () => invalidateAfterUserGroupMemberMutation({ userGroupId });

  const {
    mutateAsync: patchUser,
    error: userError,
    reset: resetUserError
  } = noddiAsync.usePatch({
    type: URLKeys.patchAccount,
    // One "<name> updated" below stands in for both requests, so the per-request
    // toast would otherwise fire twice when both halves of the sheet changed.
    queryConfig: { meta: { suppressSuccessToast: showMutationToasts }, onSuccess: invalidate }
  });
  const {
    mutateAsync: patchMember,
    error: memberError,
    reset: resetMemberError
  } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupMember,
    queryConfig: { meta: { suppressSuccessToast: showMutationToasts }, onSuccess: invalidate }
  });

  const initialValues: MemberFormValues = {
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email ?? '',
    phoneNumber: member.phoneNumber ?? '',
    smsBookingNotifications: member.smsBookingNotifications,
    smsRecallProposals: member.smsRecallProposals,
    isAdmin: member.isAdmin,
    isEmployee: member.role === UserGroupMemberRole.EMPLOYEE,
    isPrimaryContact: member.isPrimaryContact
  };

  const form = useAppForm({
    defaultValues: initialValues,
    listeners: {
      onChange: ({ formApi }) => {
        if (
          employeeForcedByAdmin(
            permissions.canAssignMembershipRole,
            formApi.getFieldValue('isAdmin'),
            formApi.getFieldValue('isEmployee')
          )
        ) {
          formApi.setFieldValue('isEmployee', true);
        }
      }
    },
    validators: { onSubmit: buildMemberSchema({ canEditAccount: permissions.canEditAccount }) },
    onSubmit: async ({ value, formApi }) => {
      resetUserError();
      resetMemberError();

      const values = keepEditedValues({
        values: value,
        initialValues,
        isEdited: (field) => formApi.getFieldMeta(field)?.isDirty ?? false
      });
      const patchArgs = { values, initialValues, permissions };
      const accountBody = buildAccountPatch(patchArgs);
      const memberBody = buildMembershipPatch(patchArgs);

      const saves: Promise<unknown>[] = [];
      if (accountBody) {
        saves.push(patchUser({ userId: member.userId, ...accountBody }));
      }
      if (Object.keys(memberBody).length > 0) {
        saves.push(patchMember({ userGroupMemberId: member.userGroupMemberId, ...memberBody }));
      }

      if (saves.length === 0) {
        onClose();
        return;
      }

      // The two halves are independent, so one failing must not cancel the other:
      // settle both and keep the sheet open on whichever failed, errors above the fields.
      const results = await Promise.allSettled(saves);
      if (results.some((result) => result.status === 'rejected')) {
        return;
      }

      if (showMutationToasts) {
        noddiToast.success(t.memberSaved(`${values.firstName} ${values.lastName}`.trim()));
      }
      onClose();
    }
  });

  return { form, userError, memberError };
};
